/**
 * C13 reports view-model — pure display shaping for the company-reports screen
 * (monthly bar scaling, quick-summary KPI derivation, and the status/search/date
 * filtering behind the report preview). Kept out of the .vue as plain CommonJS so
 * node:test can cover the logic directly (the companies repo has no SFC runner).
 * The template just binds the output. Mirrors the home-vm.js pattern; written
 * standalone (no cross-file import) so C13 owns its own helpers.
 */
// The report-type options for the C13 config panel. There are two report
// types: claims and employees (the old separate "refunds" report was removed
// in S7 — refunds are now part of claims).
var REPORT_TYPES = ['claims', 'employees'];

// S3 claim status → .tag modifier class available in vivo-components.css.
var STATUS_TAG = {
  registered: 'new',
  at_insurer: 'review',
  missing_doc: 'muted',
  accepted: 'ok',
  rejected: 'danger',
};

// Which claim statuses each report status-filter option selects. `all` = no filter.
var STATUS_FILTER = {
  all: null,
  accepted: ['accepted'],
  rejected: ['rejected'],
  review: ['registered', 'at_insurer', 'missing_doc'],
};

// Scale each month's count to a percent of the tallest bar (0..100).
function monthlyBars(employeesByMonth) {
  var rows = employeesByMonth || [];
  var max = 0;
  for (var i = 0; i < rows.length; i++) max = Math.max(max, rows[i].count);
  return rows.map(function (m) {
    return {
      month: m.month,
      count: m.count,
      pct: max > 0 ? Math.round((m.count / max) * 100) : 0,
    };
  });
}

// Derive the quick-summary KPIs from the claim activities currently in view:
// how many rows, total spend, and the acceptance rate (approved / total).
function summarize(activities) {
  var rows = activities || [];
  var count = rows.length;
  var totalSpend = 0;
  var approved = 0;
  for (var i = 0; i < rows.length; i++) {
    totalSpend += Number(rows[i].amount) || 0;
    if (rows[i].status === 'accepted') approved += 1;
  }
  return {
    count: count,
    totalSpend: totalSpend,
    acceptanceRate: count > 0 ? Math.round((approved / count) * 100) : 0,
  };
}

// Apply the report filters (status option + free-text query + date range) to the
// claim activities, returning the rows the preview table should show.
function filterRows(activities, opts) {
  var rows = activities || [];
  var o = opts || {};
  var wanted = STATUS_FILTER[o.status || 'all'];
  var q = String(o.q || '').trim().toLowerCase();
  var from = o.from || '';
  var to = o.to || '';
  return rows.filter(function (r) {
    if (wanted && wanted.indexOf(r.status) === -1) return false;
    if (q) {
      var hay = (String(r.employeeName || '') + ' ' + String(r.serviceType || '')).toLowerCase();
      if (hay.indexOf(q) === -1) return false;
    }
    var day = r.createdAt ? String(r.createdAt).slice(0, 10) : '';
    if (from && day && day < from) return false;
    if (to && day && day > to) return false;
    return true;
  });
}

function tagFor(status) {
  return STATUS_TAG[status] || 'muted';
}

// ---------------------------------------------------------------------------
// Report jobs: POST /reports { type, format, params } -> { id, status, resultRef,
// format, contentType, fileName }; GET /reports/:id; download GET /files/:resultRef.
// ---------------------------------------------------------------------------
var REPORT_FORMATS = ['csv', 'xlsx', 'pdf'];
var JOB_FAILED = ['failed', 'error'];
var JOB_PENDING = ['queued', 'processing', 'pending'];
var DEFAULT_CONTENT_TYPE = {
  csv: 'text/csv;charset=utf-8',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};

/**
 * Server-side `status` param for the claims report ('all' = no filter).
 *
 * `review` is a GROUP, not a stored status: the backend expands it to
 * registered | at_insurer | missing_doc (reports.service.ts OPEN_STATUSES). It
 * used to be forwarded to a plain equality match, which hit zero rows — and the
 * job still completed `done`, so the user downloaded a header-only file with no
 * explanation. Keep this in step with the backend's expansion.
 */
function serverStatus(statusFilter) {
  return statusFilter === 'accepted' || statusFilter === 'rejected' || statusFilter === 'review'
    ? statusFilter
    : null;
}

/**
 * POST /reports body from the config panel. `from`/`to`/`status` only ride in
 * params when set; `status` applies to the claims report only (employees ignore it).
 */
function buildReportRequest(opts) {
  var o = opts || {};
  var type = REPORT_TYPES.indexOf(o.type) !== -1 ? o.type : 'claims';
  var format = REPORT_FORMATS.indexOf(o.format) !== -1 ? o.format : 'csv';
  var params = {};
  if (o.from) params.from = o.from;
  if (o.to) params.to = o.to;
  var st = type === 'claims' ? serverStatus(o.status) : null;
  if (st) params.status = st;
  return { type: type, format: format, params: params };
}

function jobPhase(report) {
  var st = report && report.status;
  if (!st) return 'none';
  if (st === 'done') return report.resultRef ? 'done' : 'failed';
  if (JOB_FAILED.indexOf(st) !== -1) return 'failed';
  if (JOB_PENDING.indexOf(st) !== -1) return 'pending';
  return 'failed';
}

/** File name + content type for the download (server values win, with safe fallbacks). */
function downloadMeta(report) {
  var r = report || {};
  var format = REPORT_FORMATS.indexOf(r.format) !== -1 ? r.format : 'csv';
  return {
    fileName: r.fileName || 'report-' + (r.id != null ? r.id : 'file') + '.' + format,
    contentType: r.contentType || DEFAULT_CONTENT_TYPE[format],
  };
}

/** Path of the authenticated report file (the signed resultRef is URL-encoded). */
function filePath(report) {
  return '/files/' + encodeURIComponent(String(report && report.resultRef));
}

/**
 * Follow a not-yet-finished job to a terminal state. Generation is synchronous
 * today, so this normally returns on the first check; kept so an async backend
 * keeps working. fetchOne(id) -> report; sleep(ms) injected for tests.
 */
async function pollReport(report, fetchOne, sleep, maxTries) {
  var current = report;
  var tries = maxTries == null ? 20 : maxTries;
  while (jobPhase(current) === 'pending' && tries > 0) {
    tries -= 1;
    await sleep(1500);
    current = await fetchOne(current.id);
  }
  return current;
}

/** Axios error -> i18n suffix under reports.errors.*. */
function reportErrorKey(err) {
  var res = err && err.response;
  if (!res) return 'network';
  if (res.status === 403) return 'forbidden';
  if (res.status === 400) return 'invalid';
  return 'generic';
}

module.exports = {
  REPORT_FORMATS: REPORT_FORMATS,
  buildReportRequest: buildReportRequest,
  serverStatus: serverStatus,
  jobPhase: jobPhase,
  downloadMeta: downloadMeta,
  filePath: filePath,
  pollReport: pollReport,
  reportErrorKey: reportErrorKey,
  REPORT_TYPES: REPORT_TYPES,
  monthlyBars: monthlyBars,
  summarize: summarize,
  filterRows: filterRows,
  tagFor: tagFor,
  STATUS_TAG: STATUS_TAG,
  STATUS_FILTER: STATUS_FILTER,
};
